import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Templates } from '@hellohair/types';
import { FC, ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseButton } from '../../../button-components/close-button/CloseButton';

/** A labelled text input with the editor's shared sizing. */
export const StepField: FC<{
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  multiline?: boolean;
  helperText?: string;
}> = ({ label, value, onChange, multiline, helperText }) => (
  <TextField
    label={label}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    helperText={helperText}
    multiline={multiline}
    fullWidth
    size="small"
    margin="dense"
  />
);

/** A labelled dropdown with the editor's shared sizing. */
export const StepSelect: FC<{
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  children: ReactNode;
  minWidth?: string;
  helperText?: string;
}> = ({ label, value, onChange, children, minWidth, helperText }) => {
  // A list of rows renders the same label many times - useId keeps each pairing unique.
  const labelId = useId();
  return (
    <FormControl fullWidth margin="dense" sx={minWidth ? { minWidth } : undefined}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={(e) => onChange(String(e.target.value))}
        fullWidth
        size="small"
      >
        {children}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

/**
 * Picks the step to jump to. Options are prefixed with the step id so long
 * question texts stay distinguishable in the list.
 */
export const NextQuestionSelect: FC<{
  steps: Templates.StepData[];
  value: number | undefined;
  onChange: (followerId: number) => void;
  label?: string;
}> = ({ steps, value, onChange, label }) => {
  const { t } = useTranslation();

  return (
    <StepSelect
      label={label ?? t('editor.field.nextQuestion')}
      value={value ?? -1}
      onChange={(next) => onChange(Number(next))}
    >
      <MenuItem value={-1}>{t('editor.field.nextQuestionPlaceholder')}</MenuItem>
      {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
      {steps.map((item: any) => (
        <MenuItem key={item.id} value={item.id}>
          {`#${item.id} - ${item.question || t(`editor.type.${item.type}`)}`}
        </MenuItem>
      ))}
    </StepSelect>
  );
};

/**
 * Picks the step's semantic tag from the tags something downstream actually
 * reads (`Templates.KNOWN_SEARCH_IDS`) - a free-typed tag would silently never
 * match and the field it feeds would arrive empty. A tag stored before this
 * was a dropdown is kept as an extra option, so re-saving cannot drop it.
 */
export const SearchIdSelect: FC<{
  value: string | undefined;
  onChange: (searchID: string | undefined) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const legacy = value && !Templates.KNOWN_SEARCH_IDS.includes(value) ? value : undefined;

  return (
    <StepSelect
      label={t('editor.field.searchId')}
      value={value ?? ''}
      onChange={(next) => onChange(next === '' ? undefined : next)}
      helperText={t('editor.hint.searchIdSelect')}
    >
      <MenuItem value="">{t('editor.field.searchIdNone')}</MenuItem>
      {Templates.KNOWN_SEARCH_IDS.map((id) => (
        <MenuItem key={id} value={id}>
          {`${t(`editor.searchId.${id}`, { defaultValue: id })} (${id})`}
        </MenuItem>
      ))}
      {legacy && <MenuItem value={legacy}>{t('editor.field.searchIdUnknown', { value: legacy })}</MenuItem>}
    </StepSelect>
  );
};

/** A titled block of fields, with an optional explanatory hint. */
export const FieldGroup: FC<{ title: string; hint?: string; children: ReactNode }> = ({ title, hint, children }) => (
  <Box sx={{ marginTop: '12px' }}>
    <Typography variant="body2" fontWeight={600}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: '4px' }}>
        {hint}
      </Typography>
    )}
    {children}
  </Box>
);

/** The editor's shared "remove this entry" button. */
export const RemoveButton: FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  <Button onClick={onRemove} size="small" color="error" startIcon={<DeleteOutlineIcon />}>
    {label}
  </Button>
);

/**
 * One entry of a repeatable list: its fields on a bordered row with a remove
 * button, so nested lists (answers, badges, modals, …) stay visually grouped.
 */
export const RepeatableRow: FC<{ onRemove: () => void; removeLabel?: string; children: ReactNode }> = ({
  onRemove,
  removeLabel,
  children,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: removeLabel ? 'stretch' : 'flex-start',
      gap: 1,
      padding: '4px 4px 4px 8px',
      marginTop: '4px',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '4px',
    }}
  >
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
      {children}
    </Box>
    {removeLabel ? (
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <RemoveButton label={removeLabel} onRemove={onRemove} />
      </Box>
    ) : (
      <CloseButton onClick={onRemove} />
    )}
  </Box>
);

/**
 * The folded label of a `CollapsibleRow`: a monospace chip carrying the entry's
 * identifier, the entry's title next to it and an optional trailing note.
 * `muted` greys the chip out while the entry has no identifier yet.
 */
export const RowSummary: FC<{ badge: string; muted?: boolean; title?: string; note?: string }> = ({
  badge,
  muted,
  title,
  note,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Chip
      size="small"
      label={badge}
      sx={{
        flexShrink: 0,
        height: '20px',
        fontFamily: 'monospace',
        backgroundColor: 'transparent',
        border: '1px solid',
        borderColor: 'divider',
        color: muted ? 'text.secondary' : 'text.primary',
      }}
    />
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {title}
    </Typography>
    {note && (
      <Typography variant="body2" sx={{ color: 'text.secondary', flexShrink: 0 }}>
        {note}
      </Typography>
    )}
  </Box>
);

/**
 * One entry of a repeatable list whose fields are too tall to keep unfolded:
 * collapsed it only shows `summary`, expanded it shows the fields and a remove
 * button. Expansion is controlled by the list so only one entry is open at a time.
 */
export const CollapsibleRow: FC<{
  summary: ReactNode;
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  removeLabel: string;
  onRemove: () => void;
  children: ReactNode;
}> = ({ summary, expanded, onToggle, removeLabel, onRemove, children }) => (
  <Accordion
    expanded={expanded}
    onChange={(_event, isExpanded) => onToggle(isExpanded)}
    disableGutters
    elevation={0}
    sx={{
      marginTop: '4px',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '4px',
      '&::before': { display: 'none' },
      '&.Mui-expanded': { margin: '4px 0 0' },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{ minHeight: '40px', '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
    >
      {summary}
    </AccordionSummary>
    <AccordionDetails sx={{ paddingTop: 0 }}>
      {children}
      <Box sx={{ marginTop: '8px' }}>
        <RemoveButton label={removeLabel} onRemove={onRemove} />
      </Box>
    </AccordionDetails>
  </Accordion>
);

export const AddRowButton: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Button
    onClick={onClick}
    fullWidth
    variant="outlined"
    size="small"
    startIcon={<AddCircleOutlineIcon />}
    sx={{ marginTop: '6px' }}
  >
    {label}
  </Button>
);

export const StepCheckbox: FC<{ label: string; checked: boolean; onChange: (checked: boolean) => void }> = ({
  label,
  checked,
  onChange,
}) => (
  <FormControlLabel
    control={<Checkbox size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />}
    label={<Typography variant="body2">{label}</Typography>}
    sx={{ whiteSpace: 'nowrap' }}
  />
);
